"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { Ethereum, X } from "@/components";
import { SecondaryFooter } from "./secondary-footer";
import {
  Square2StackIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { usePathname } from "next/navigation";
import { useCopyToClipboard } from "usehooks-ts";
import { toast } from "sonner";

const DONATION_ADDRESS = "0x618B6Cf82c44C4f2e7997f4498730195259E215b";
const DONATION_ADDRESS_DISPLAY = `${DONATION_ADDRESS.slice(0, 6)}....${DONATION_ADDRESS.slice(-4)}`;
const DONATION_URL =
  "https://www.lightbeam.xyz/holdings/0x618b6cf82c44c4f2e7997f4498730195259e215b";

const ADDRESS_LINK_CLASS =
  "shrink-0 whitespace-nowrap font-mono text-xs tracking-wide text-white transition-colors duration-300 hover:text-hex-400";

function DonationAddressPill() {
  const [_, copy] = useCopyToClipboard();
  const [isCopied, setIsCopied] = React.useState(false);
  const [useAbbreviated, setUseAbbreviated] = React.useState(false);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const linkRef = React.useRef<HTMLAnchorElement>(null);

  React.useEffect(() => {
    if (isCopied) {
      const t = setTimeout(() => setIsCopied(false), 4000);
      return () => clearTimeout(t);
    }
  }, [isCopied]);

  const evaluateFit = React.useCallback(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    const pill = row?.parentElement;
    const copyColumn = row?.closest(".donation-copy-column");
    if (!row || !measure || !pill || !copyColumn) return;

    const button = row.querySelector("button");
    if (!button) return;

    const columnWidth = copyColumn.clientWidth;
    if (columnWidth === 0) return;

    const gap = parseFloat(getComputedStyle(row).columnGap) || 6;
    const buttonWidth = button.getBoundingClientRect().width;
    const pillStyles = getComputedStyle(pill);
    const pillPaddingX =
      parseFloat(pillStyles.paddingLeft) + parseFloat(pillStyles.paddingRight);
    const rowBudget = Math.max(0, columnWidth - pillPaddingX);

    const measureRowWidth = (text: string) => {
      measure.textContent = text;
      measure.style.whiteSpace = "nowrap";
      measure.style.maxWidth = "none";
      measure.style.width = "auto";
      measure.style.wordBreak = "normal";
      return measure.scrollWidth + buttonWidth + gap;
    };

    const fullRowWidth = measureRowWidth(DONATION_ADDRESS);
    let needsAbbrev = fullRowWidth > rowBudget;

    const link = linkRef.current;
    if (link) {
      const lineHeight = parseFloat(getComputedStyle(link).lineHeight) || 16;
      const linkRect = link.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const overlapsButton = linkRect.right > buttonRect.left - 1;
      const wrapsLink = link.scrollHeight > lineHeight * 1.25;
      const overflowsLink = link.scrollWidth > link.clientWidth + 1;

      if (overlapsButton || overflowsLink || wrapsLink) {
        needsAbbrev = true;
      }
    }

    setUseAbbreviated(needsAbbrev);
  }, []);

  React.useLayoutEffect(() => {
    evaluateFit();
    const raf = requestAnimationFrame(evaluateFit);

    const observer = new ResizeObserver(() => evaluateFit());
    const row = rowRef.current;
    if (!row) {
      return () => cancelAnimationFrame(raf);
    }

    observer.observe(row);
    if (row.parentElement) observer.observe(row.parentElement);

    const layoutRoot = row.closest(".donation-support-layout");
    if (layoutRoot) observer.observe(layoutRoot);

    const copyColumn = row.closest(".donation-copy-column");
    if (copyColumn) observer.observe(copyColumn);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [evaluateFit, useAbbreviated]);

  const displayAddress = useAbbreviated
    ? DONATION_ADDRESS_DISPLAY
    : DONATION_ADDRESS;

  return (
    <div className="mx-auto w-fit max-w-full rounded-xl border border-white/[0.08] bg-surface-300/35 px-4 py-3 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.07)] backdrop-blur-sm sm:mx-0 lg:mx-auto xl:mx-0">
      <div
        ref={rowRef}
        className="relative inline-flex max-w-full flex-nowrap items-center justify-center gap-x-1.5 sm:justify-start"
      >
        <span
          ref={measureRef}
          aria-hidden
          className={`pointer-events-none invisible absolute left-0 top-0 ${ADDRESS_LINK_CLASS}`}
        />
        <Link
          ref={linkRef}
          target="_blank"
          rel="noopener noreferrer"
          href={DONATION_URL}
          title={DONATION_ADDRESS}
          className={ADDRESS_LINK_CLASS}
        >
          {displayAddress}
        </Link>
        <button
          type="button"
          aria-label="Copy donation address"
          onClick={() => {
            setIsCopied(true);
            copy(DONATION_ADDRESS);
            toast.success("Address copied.");
          }}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/10 bg-surface-400/40 text-muted transition-colors hover:border-hex-400/30 hover:text-hex-400"
        >
          {isCopied ? (
            <CheckIcon className="h-4 w-4 stroke-2" />
          ) : (
            <Square2StackIcon className="h-4 w-4 stroke-2" />
          )}
        </button>
      </div>
    </div>
  );
}

const CHAIN_NAMES = [
  {
    label: "lookintohex.eth",
    href: "https://app.ens.domains/lookintohex.eth",
    kind: "ethereum" as const,
  },
  {
    label: "gerardo.pls",
    href: "https://app.pulse.domains/0x618B6Cf82c44C4f2e7997f4498730195259E215b",
    kind: "pulsechain" as const,
  },
] as const;

export function Footer() {
  const pathname = usePathname();

  if (pathname.startsWith("/auth")) {
    return null;
  }

  return (
    <>
      <footer className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="lhx-card relative flex flex-col justify-between gap-6 overflow-hidden p-6">
          <Image
            src="https://pbs.twimg.com/media/GTJj1JTbsAAhEK4?format=jpg&name=large"
            alt=""
            width={768}
            height={768}
            className="absolute right-0 top-0 h-full w-auto opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary via-60% to-primary/50 sm:via-85% lg:via-70%" />
          <div className="relative z-10">
            <h2 className="mb-2 text-2xl font-bold tracking-tight text-white">
              Created by Gerardo
            </h2>
            <p className="text-base font-normal text-foreground">
              I hope you guys love what I&apos;ve built for Pulsechain.
            </p>
          </div>
          <Link
            target="_blank"
            href="https://twitter.com/gerawrdog"
            className="relative z-10 flex items-center gap-4 text-white transition-colors duration-300 hover:text-hex-400"
          >
            Follow on <X />
          </Link>
        </div>

        <div className="lhx-card relative flex min-h-[16rem] flex-col overflow-hidden border-white/[0.08] bg-surface-200/55 p-6">
          <div
            className="pointer-events-none absolute -left-16 top-0 h-52 w-52 rounded-full bg-hex-400/[0.08] blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-10 -right-10 h-56 w-56 rounded-full bg-[#7880E7]/10 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-52 bg-gradient-to-t from-primary/25 via-primary/5 to-transparent"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
            aria-hidden
          />

          <div className="donation-support-layout relative z-10 flex flex-1 flex-col items-center gap-8 text-center sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:text-left lg:flex-col lg:items-center lg:text-center xl:flex-row xl:items-center xl:justify-between xl:text-left">
            <div className="donation-copy-column flex w-full min-w-0 flex-1 flex-col items-center gap-5 sm:items-start lg:items-center xl:items-start">
              <div>
                <span className="label-caps mb-2 block text-hex-400/90">
                  Community supported
                </span>
                <h2 className="mb-2 text-2xl font-bold tracking-tight text-white">
                  Support the site
                </h2>
                <p className="mx-auto max-w-md text-base font-normal leading-relaxed text-foreground sm:mx-0 lg:mx-auto xl:mx-0">
                  Built &amp; maintained for free — tips help keep the lights on.
                </p>
              </div>

              <DonationAddressPill />
            </div>

            <div className="flex shrink-0 items-center justify-center gap-10 sm:justify-end lg:justify-center xl:justify-end">
              {CHAIN_NAMES.map(({ label, href, kind }: (typeof CHAIN_NAMES)[number]) => (
                <Link
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col items-center justify-center gap-2.5"
                >
                  <div className="flex h-[7.5rem] w-[7.5rem] items-center justify-center rounded-2xl border border-white/[0.08] bg-surface-300/30 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.07)] backdrop-blur-sm transition-all duration-300 group-hover:border-hex-400/25 group-hover:bg-surface-300/50 group-hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1),0_0_28px_rgba(0,255,136,0.12)]">
                    {kind === "ethereum" ? (
                      <Ethereum
                        className="h-[5.5rem] w-[5.5rem] shrink-0"
                        muted={false}
                      />
                    ) : (
                      <Image
                        src="/pulsechain.png"
                        alt=""
                        width={220}
                        height={220}
                        className="h-[5.5rem] w-auto max-w-[5.75rem] shrink-0 object-contain"
                      />
                    )}
                  </div>
                  <span className="font-mono text-2xs tracking-wide text-muted transition-colors group-hover:text-hex-400">
                    {label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <SecondaryFooter />
    </>
  );
}

export default Footer;
